import React from 'react';
import { Navigate, NavLink } from 'react-router-dom';

import IconPlant from '@/assets/icon-plant.svg?react';
import IconValue from '@/assets/icon-value.svg?react';
import IconReports from '@/assets/icon-reports.svg?react';
import IconStacked from '@/assets/icon-stacked.svg?react';
import { Auth } from '@/comp';
import { permissions } from '@/constants';
import { closeSocket } from '@/sockets/socket';
import ServiceIcon from '@/comp/common/icons/ServiceIcon';
import LifecycleIcon from '@/comp/common/icons/LifecycleIcon';
import ReportIcon from '@/comp/common/icons/ReportIcon';
import { StationStageDto } from '@/store/Station/constants';
import { LifecycleStageId } from '../LifecyclePage/constants';
import AISparkleIcon from '@/comp/common/icons/AISparkleIcon';

export interface NavItemDetails {
  icon: JSX.Element;
  link?: string;
  label?: string;
  permissions?: permissions;
}

export const getTopNavItems = (
  t: (key: string) => string,
  pathname: string,
  isMonitorStationTierForPeriodicReports?: boolean,
  currentStationstage?: StationStageDto,
): NavItemDetails[] => {
  const items: NavItemDetails[] = [
    {
      icon: <IconPlant className="icon" />,
      label: t('PLANT'),
      link: 'tab',
    },
    {
      icon: <IconValue className="icon" />,
      label: t('VALUE'),
      link: 'value',
      permissions: permissions.VIEW_STATION_VALUE_DASHBOARD,
    },
    {
      icon: <IconReports className="icon-reports" />,
      label: t('REPORTS'),
      link: 'reports',
      permissions: permissions.VIEW_STATION_REPORTS,
    },
    {
      icon: <IconStacked className={`icon-engine ${pathname.endsWith('centrifugal') ? 'active' : ''}`} />,
      label: t('ENGINE'),
      link: 'engine',
      permissions: permissions.VIEW_STATION_ENGINE,
    },
    {
      icon: <ServiceIcon className="icon" />,
      label: t('Installer'),
      link: 'installer',
      permissions: permissions.ACCESS_INSTALLATION_VALIDATION_VIEW,
    },
    {
      icon: <LifecycleIcon className="icon" />,
      label: t('Lifecycle'),
      link: 'lifecycle',
      permissions: permissions.VIEW_STATION_STAGE_LIFECYCLE,
    },
    {
      icon: <AISparkleIcon className="icon" />,
      label: t('Assistant'),
      link: 'ai-assistant',
      permissions: permissions.VIEW_SALES_ASSISTANT,
    },
  ];

  // Only show Report tab if station tier is loaded and not Monitor
  if (
    !isMonitorStationTierForPeriodicReports &&
    isMonitorStationTierForPeriodicReports !== null &&
    [
      LifecycleStageId.Subscription,
      LifecycleStageId.Baseline,
      LifecycleStageId.ControlPrep,
      LifecycleStageId.Deprecated,
    ].includes(currentStationstage?.stage?.id)
  ) {
    items.push({
      icon: <ReportIcon className="icon" />,
      label: t('Report'),
      link: 'report-tab',
      permissions: permissions.VIEW_PERIODIC_REPORTS,
    });
  }

  return items;
};

export const parseNavItem = (params: NavItemDetails) => {
  const itemBody = (
    <NavLink to={params.link} className={({ isActive }) => (isActive ? 'active' : '')}>
      {params.icon}
      {params.label && <h3 className="label">{params.label}</h3>}
    </NavLink>
  );

  if (params.permissions) return <Auth permission={params.permissions}>{itemBody}</Auth>;

  return itemBody;
};

export const signOutHandler = (userStore, navigate) => async () => {
  closeSocket();
  await userStore.signOut();
  navigate('/login');
};

export const NavigateWithParams = (to: string, replace = false, searchParams: URLSearchParams) => {
  const searchString = searchParams.toString();

  const targetPath = {
    pathname: to,
    search: searchString ? `?${searchString}` : '',
  };

  return <Navigate replace={replace} to={targetPath} />;
};
